import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import styled from '@emotion/styled';

import { Box, Button, COLORS, Input, Label } from '@clutter/clean';

import { Card } from '@shared/components/bootstrap';
import { valid } from '@shared/config/password';

import { Form } from '@portal/components/shared/customer_protected/form';
import { Settings__UpdatePasswordInput, useUpdatePasswordMutation } from '@portal/schema';

import { PasswordMatches } from '../password/matches';
import { PasswordStrength } from '../password/strength';
import { Message } from './formatters';

const Right = styled.div`
  text-align: right;
`;

const Field: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => (
  <Box.Flex flexDirection="column" gap="4px">
    {children}
  </Box.Flex>
);

export const Password: React.FC = () => {
  const { register, handleSubmit, watch, reset } = useForm<Settings__UpdatePasswordInput>();
  const [message, setMessage] = useState<string | undefined>();
  const [success, setSuccess] = useState(false);

  const [updatePassword, { loading }] = useUpdatePasswordMutation();

  const password = watch('password') ?? '';
  const passwordConfirmation = watch('passwordConfirmation') ?? '';

  const disabled = !valid(password) || !valid(passwordConfirmation) || password !== passwordConfirmation;

  const onSubmit = async (values: Settings__UpdatePasswordInput) => {
    if (loading) return;
    if (disabled) return;

    const result = await updatePassword({
      variables: {
        input: {
          currentPassword: values.currentPassword,
          password: values.password,
          passwordConfirmation: values.passwordConfirmation,
        },
      },
    });

    if (result.data?.updatePassword?.error) {
      setSuccess(false);
      setMessage(result.data?.updatePassword?.error);
    } else {
      setSuccess(true);
      setMessage('Password updated successfully');
      reset();
    }
  };

  const onActionBlocked = (errorMessage: string) => {
    setMessage(errorMessage);
  };

  return (
    <Card>
      <Form onSubmit={handleSubmit(onSubmit)} onActionBlocked={onActionBlocked} id="passwords">
        <Card.Header>Update Password</Card.Header>
        <Card.Body>
          <Box.Flex flexDirection="column" gap="24px">
            <Field>
              <Label htmlFor="currentPassword">Current Password</Label>
              <Input
                required
                ref={register()}
                type="password"
                id="currentPassword"
                name="currentPassword"
                autoComplete="current-password"
              />
            </Field>
            <Field>
              <Label htmlFor="password">New Password</Label>
              <Input
                required
                ref={register()}
                type="password"
                id="password"
                name="password"
                autoComplete="new-password"
              />
              <PasswordStrength password={password} />
            </Field>
            <Field>
              <Label htmlFor="passwordConfirmation">Confirm New Password</Label>
              <Input
                ref={register()}
                type="password"
                id="passwordConfirmation"
                name="passwordConfirmation"
                autoComplete="new-password"
              />
              <PasswordMatches password={password} confirmation={passwordConfirmation} />
            </Field>
          </Box.Flex>
          <Message color={success ? COLORS.tealPrimary : COLORS.toucan}>{message}</Message>
        </Card.Body>
        <Card.Footer>
          <Right>
            <Button type="submit" kind="primary" loading={loading} disabled={disabled}>
              Save
            </Button>
          </Right>
        </Card.Footer>
      </Form>
    </Card>
  );
};
